// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import differenceInDays from 'date-fns/differenceInDays';
import { startOfDay } from 'date-fns';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LiliLinkButton, LiliLinkType } from '@lili/lili-frontend-common';
import { formatPhoneNum } from '../../services/phoneNumber.service';
import { Layout } from '../../components/Layout/Layout';
import { AccountSettingsLegalDocs } from './components/AccountSettingsLegalDocs';
import { SettingsItem } from './components/AccountSettingsItem';
import { AccountSettingsPlanItem } from './components/AccountSettingsPlanItem';
import { AccountSettingsHeader } from './components/AccountSettingsHeader';
import { useStore } from '../../store';
import { ISummaryData, RoleType, TeamMember } from '../../types';
import { AccountSettingsSupport, SupportItemType } from './components/AccountSettingsSupport';
import { AccountSettingsSection } from './components/AccountSettingsSection';
import { VIEWS_MAP } from '../../router/constants';
import { AccountSettingsFlow, AccountSettingsFlowManager } from './components/AccountSettingsFlowManager';
import { ReactComponent as PlusIcon } from '../../assets/svg/invoices-icons/icon-plus.svg';

import { ReactComponent as ProfileIcon } from '../../assets/icons/svg/shared/ic-shared-profile.svg';
import { ReactComponent as EmailIcon } from '../../assets/icons/svg/shared/ic-shared-email.svg';
import { ReactComponent as PhoneIcon } from '../../assets/icons/svg/shared/ic-shared-phone.svg';

import { ReactComponent as LocationIcon } from '../../assets/icons/svg/shared/ic-shared-location.svg';
import { ReactComponent as BusinessIcon } from '../../assets/icons/svg/shared/ic-shared-business.svg';

import { ReactComponent as PasswordIcon } from '../../assets/icons/svg/shared/ic-shared-password.svg';
import { ReactComponent as PinCodeIcon } from '../../assets/icons/svg/shared/ic-shared-pin-code.svg';
import { canActivatePhysicalCard, canSetPin, getMostRelevantCard } from '../../helpers/cardStatus.helper';
import { AccountSettingsRolesAndPermissionsItem } from './components/rolesAndPermissions/AccountSettingsRolesAndPermissions';
import LiliToast, { LiliToastDefaultConfig } from '../../DesignSystem/LiliToast/LiliToast';
import { InviteStatus, mutateInviteStatus } from '../AccountantOnboarding/hooks/mutateInviteStatus';
import { TeamFullModal } from './components/TeamFullModal';
import { trackMixPanel } from '../../services/mixpanel.service';
import { featureAccess, LimitedFeatureType } from '../../services/appAccess.service';
import { FeatureAccess } from './components/rolesAndPermissions/PermissionsList';
import { RemoveTeamMemberModal } from './components/rolesAndPermissions/RemoveTeamMemberModal';
import { useLogoutQuery } from '../../hooks/query/useLogoutQuery';
import { FEATURE_FLAG } from '../../constants/general';
import { validateFeatureFlag } from '../../services/featureFlagService';
import { useAdaService } from '../../hooks/services/useAdaService';

export const AccountSettings = () => {
  const [currentFlow, setCurrentFlow] = useState<AccountSettingsFlow>(AccountSettingsFlow.NONE);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const supportPhone = '(855) 545-4380';
  const supportEmail = 'support@lili.co';
  const helpCenterUrl = 'https://support.lili.co/hc/en-us';
  const { getPersonalAccount } = useStore((state) => state);
  const mostSignificantCard = getMostRelevantCard(getPersonalAccount().cards);
  const canActivateCard = canActivatePhysicalCard(mostSignificantCard);
  const canSetPinCode = canSetPin(mostSignificantCard);
  const { toggleAdaChat } = useAdaService();

  const { summary, setSummary } = useStore.getState();

  const { mutateAsync: removeMember, isLoading: isRemovingMember } = mutateInviteStatus();
  const [teamMemberToDelete, setTeamMemberToDelete] = useState<TeamMember | null>(null);
  const [shouldShowTeamFullModal, setShouldShowTeamFullModal] = useState<boolean>(false);
  const {
    customer: {
      firstName,
      nationalIdDescription,
      lastName,
      email,
      phoneNumber,
      externalId: customerId,
      address: customerAddress,
      businessAddress,
      createTime,
    },
    business: { name: businessName, status },
    customerProfileInfo: { customerProfile, profileFee, annual },
    actionFlags: { businessAddressDisplay, businessAddressEnabled },
    authorizedUserData,
    accounts,
    teamMembers,
  }: ISummaryData = useStore((state) => state.summary);

  useEffect(() => {
    trackMixPanel('Page view', { pageName: 'Settings screen' });
  }, []);
  const allowInvite = () => {
    const totalAdmins = teamMembers.filter((member) => member.teamMemberType === RoleType.ADMIN);
    const totalAccountants = teamMembers.filter((member) => member.teamMemberType === RoleType.ACCOUNTANT);
    return totalAdmins.length < 3 || totalAccountants.length < 1;
  };

  const contactDetailsPermission = featureAccess(null, LimitedFeatureType.ContactDetails);
  const businessDetailsPermission = featureAccess(null, LimitedFeatureType.BusinessDetails);
  const cardActionsPermission = featureAccess(null, LimitedFeatureType.CardActions);
  const planUpgradePermission = featureAccess(null, LimitedFeatureType.UpgradePlan);
  const isAuthorizedUser = authorizedUserData !== undefined;
  const showActivateCardItem = canSetPinCode && cardActionsPermission !== FeatureAccess.NONE;
  const showChangePinItem = canActivateCard && cardActionsPermission !== FeatureAccess.NONE;
  const showPasswordItem = !isAuthorizedUser;

  const allowInviteAccountantPermission = validateFeatureFlag(FEATURE_FLAG.INVITE_ACCOUNTANT);
  const allowInviteAdminPermission = validateFeatureFlag(FEATURE_FLAG.INVITE_ADMIN);
  const shouldShowInviteButton = !isAuthorizedUser && (allowInviteAccountantPermission || allowInviteAdminPermission);

  const showAccountSecuritySection = showActivateCardItem || showChangePinItem || showPasswordItem;
  const {
    email: authUserEmail,
    phoneNumber: authPhoneNumber,
    firstName: authFirstName,
    lastName: authLastName,
  } = authorizedUserData?.authorizedUserDetails || {};
  const isUs = !nationalIdDescription;
  const displayEmail = authUserEmail || email;
  const { refetch: triggerLogout } = useLogoutQuery({
    customerId,
    onSuccess: () => {
      navigate(`/${VIEWS_MAP.LOGIN}`);
    },
  });
  const onEditEmailClick = () => {
    setCurrentFlow(AccountSettingsFlow.CHANGE_EMAIL);
  };
  const onEditPhoneClick = () => {
    setCurrentFlow(AccountSettingsFlow.CHANGE_PHONE);
  };
  const onEditPersonalAddressClick = () => {
    setCurrentFlow(AccountSettingsFlow.CHANGE_ADDRESS);
  };
  const onAddBusinessNameClick = () => {
    // TODO: Implement Add business name flow
  };
  const onEditBusinessAddressClick = () => {
    setCurrentFlow(AccountSettingsFlow.CHANGE_BUSINESS_ADDRESS);
  };
  const onEditPasswordClick = () => {
    setCurrentFlow(AccountSettingsFlow.CHANGE_PASSWORD);
  };

  const onActivateCardClick = () => {
    navigate(`/${VIEWS_MAP.ACTIVATE_CARD}`, {
      state: { backDestination: VIEWS_MAP.ACCOUNT_SETTINGS },
    });
  };

  const onChangeCardPinClick = () => {
    navigate(`/${VIEWS_MAP.CHANGE_PIN}`, {
      state: { backDestination: VIEWS_MAP.ACCOUNT_SETTINGS },
    });
  };

  const onUpgradePlanClick = () => {
    setCurrentFlow(AccountSettingsFlow.PLAN_UPGRADE);
  };

  const onEmailClick = () => {
    window.open(`mailto:${supportEmail}`, '_blank');
  };
  const onCallUsClick = () => {
    window.open(`tel:${supportPhone}`, '_blank');
  };
  const onChatClick = () => {
    toggleAdaChat();
  };
  const onHelpCenterClick = () => {
    window.open(helpCenterUrl, '_blank');
  };

  const onSupportItemClick = (type: SupportItemType) => {
    switch (type) {
      case SupportItemType.EMAIL:
        onEmailClick();
        break;
      case SupportItemType.CALL:
        onCallUsClick();
        break;
      case SupportItemType.CHAT:
        onChatClick();
        break;
      case SupportItemType.HELP_CANTER:
        onHelpCenterClick();
        break;
      default:
        break;
    }
  };

  const onInviteClick = () => {
    trackMixPanel('Act on settings screen', { Action: 'Invite a team member' });
    if (allowInvite()) {
      navigate(`/${VIEWS_MAP.INVITE_ACCOUNTANT.INVITE}`);
    } else {
      setShouldShowTeamFullModal(true);
    }
  };

  const onRemoveMemberClick = (teamMember: TeamMember) => {
    setTeamMemberToDelete(teamMember);
  };
  const isAllowedToChangeSettings = differenceInDays(startOfDay(new Date()), startOfDay(new Date(createTime))) > 30;

  const onRemoveTeamMember = (teamMember: TeamMember) => {
    removeMember({
      authorizedUserId: teamMember.externalId,
      inviteStatus: InviteStatus.CANCELED,
      customerId,
      isAuthorizedUser,
    })
      .then(() => {
        const modifiedTeamMembers = teamMembers.filter((member) => member.externalId !== teamMember.externalId);
        setSummary({ ...summary, teamMembers: modifiedTeamMembers });
        setTeamMemberToDelete(null);
        toast(
          <LiliToast
            id='remove-team-member-toast'
            text={t('ACCOUNT_SETTINGS.TEAM.USER_REMOVED_NOTIFICATION', {
              userName:
                teamMember.firstName || teamMember.lastName
                  ? `${teamMember.firstName || ''} ${teamMember.lastName || ''}`
                  : teamMember.email,
            })}
          />,
          { ...LiliToastDefaultConfig },
        );
        if (teamMember.isCurrentlyLoggedInUser) {
          triggerLogout();
        }
      })
      .catch(() => {
        // setTeamMemberToDelete(null);
        // TODO: Show error
      });
  };

  const onRemoveSubmit = () => {
    onRemoveTeamMember(teamMemberToDelete);
  };
  const formattedAddress = (address) =>
    [address?.line1, address?.line2, address?.city, address?.state, address?.postalCode].filter(Boolean).join(', ');
  const formattedPhoneValue = authPhoneNumber ? formatPhoneNum(authPhoneNumber) : formatPhoneNum(phoneNumber);

  const teamFullModal = shouldShowTeamFullModal && <TeamFullModal onClick={() => setShouldShowTeamFullModal(false)} />;
  return (
    <Layout
      childrenWrapperClassName='w-full h-full flex flex-col items-center bg-surface-primary-bw-01'
      wrapperClassName='flex flex-col w-full h-full items-center ml-[100px] pc-md:ml-[256px]'
    >
      <div className='flex flex-col items-center h-[100vh] overflow-y-scroll w-full pt-[84px]'>
        <div className=' flex flex-col items-center  max-w-[1300px] gap-[16px] w-full px-[24px]'>
          <div className='flex flex-col pb-[32px] gap-[16px] w-full'>
            <p className='text-bw08 text-[32px] text-left w-full font-metricRMedium '>{t('ACCOUNT_SETTINGS.TITLE')}</p>
            <AccountSettingsHeader
              businessName={businessName || `${authFirstName || firstName} ${authLastName || lastName}`}
              customerId={customerId}
              accountNumber={accounts[0].achAccountNumber}
            />
            <AccountSettingsSection title={t('ACCOUNT_SETTINGS.PERSONAL_INFO_SECTION')}>
              <SettingsItem
                title={t('ACCOUNT_SETTINGS.FULL_NAME')}
                value={`${authFirstName || firstName} ${authLastName || lastName}`}
                showEditButton={false}
                editButtonId='fullName'
                icon={<ProfileIcon className='text-bw08' />}
              />
              {contactDetailsPermission !== FeatureAccess.NONE && (
                <SettingsItem
                  title={t('ACCOUNT_SETTINGS.EMAIL')}
                  value={displayEmail}
                  onEditClick={onEditEmailClick}
                  editButtonId='email'
                  showEditButton={!isAuthorizedUser && isAllowedToChangeSettings}
                  icon={<EmailIcon className='text-bw08' />}
                />
              )}
              {contactDetailsPermission !== FeatureAccess.NONE && (
                <SettingsItem
                  title={t('ACCOUNT_SETTINGS.PHONE')}
                  value={formattedPhoneValue}
                  onEditClick={onEditPhoneClick}
                  showEditButton={!isAuthorizedUser && isAllowedToChangeSettings}
                  editButtonId='phone'
                  isLast={isAuthorizedUser && isAllowedToChangeSettings}
                  icon={<PhoneIcon className='text-bw08' />}
                />
              )}

              {contactDetailsPermission !== FeatureAccess.NONE && !isAuthorizedUser && (
                <SettingsItem
                  title={t('ACCOUNT_SETTINGS.PERSONAL_ADDRESS')}
                  value={formattedAddress(customerAddress)}
                  onEditClick={onEditPersonalAddressClick}
                  isLast
                  showEditButton={isUs && !isAuthorizedUser && isAllowedToChangeSettings}
                  editButtonId='personalAddress'
                  icon={<LocationIcon className='text-bw08' />}
                />
              )}
            </AccountSettingsSection>
            {status === 'APPROVED' && businessDetailsPermission !== FeatureAccess.NONE && (
              <AccountSettingsSection title={t('ACCOUNT_SETTINGS.BUSINESS_INFO_SECTION')}>
                <SettingsItem
                  title={t('ACCOUNT_SETTINGS.BUSINESS_NAME')}
                  value={businessName || t('ACCOUNT_SETTINGS.ADD_BUSINESS_NAME_DESCRIPTION')}
                  onEditClick={onAddBusinessNameClick}
                  editButtonTitle={!businessName && 'Add'}
                  showEditButton={false}
                  editButtonId='businessName'
                  isLast={!businessAddressDisplay || !businessAddress}
                  icon={<BusinessIcon className='text-bw08' />}
                />
                {businessAddressDisplay && businessAddress && (
                  <SettingsItem
                    title={t('ACCOUNT_SETTINGS.BUSINESS_ADDRESS')}
                    value={formattedAddress(businessAddress)}
                    onEditClick={onEditBusinessAddressClick}
                    isLast
                    editButtonId='businessAddress'
                    showEditButton={
                      isAllowedToChangeSettings &&
                      businessAddressEnabled &&
                      businessDetailsPermission === FeatureAccess.READ_WRITE
                    }
                    icon={<LocationIcon className='text-bw08' />}
                  />
                )}
              </AccountSettingsSection>
            )}
            {showAccountSecuritySection && (
              <AccountSettingsSection title={t('ACCOUNT_SETTINGS.ACCOUNT_SECURITY_SECTION')}>
                {showPasswordItem && (
                  <SettingsItem
                    title={t('ACCOUNT_SETTINGS.PASSWORD')}
                    value='**************'
                    onEditClick={onEditPasswordClick}
                    editButtonTitle={t('ACCOUNT_SETTINGS.CHANGE_BUTTON')}
                    editButtonId='password'
                    icon={<PasswordIcon className='text-bw08' />}
                    showEditButton
                    isLast={!showActivateCardItem && !showChangePinItem}
                  />
                )}
                {canSetPinCode && cardActionsPermission !== FeatureAccess.NONE && (
                  <SettingsItem
                    title={t('ACCOUNT_SETTINGS.PIN_CODE')}
                    value='*****'
                    onEditClick={onChangeCardPinClick}
                    editButtonTitle={t('ACCOUNT_SETTINGS.CHANGE_BUTTON')}
                    editButtonId='changePin'
                    isLast
                    showEditButton={cardActionsPermission === FeatureAccess.READ_WRITE}
                    icon={<PinCodeIcon className='text-bw08' />}
                  />
                )}
                {canActivateCard && cardActionsPermission !== FeatureAccess.NONE && (
                  <SettingsItem
                    title={t('ACCOUNT_SETTINGS.ACTIVATE_CARD')}
                    value={t('ACCOUNT_SETTINGS.ACTIVATE_CARD_DESCRIPTION')}
                    onEditClick={onActivateCardClick}
                    editButtonId='activate'
                    editButtonTitle={t('ACCOUNT_SETTINGS.ACTIVATE_CARD_BUTTON')}
                    isLast
                    showEditButton={cardActionsPermission === FeatureAccess.READ_WRITE}
                    icon={<PinCodeIcon className='text-bw08' />}
                  />
                )}
              </AccountSettingsSection>
            )}

            {/* //TODO: Need to wrap this also with a feature flag */}
            <AccountSettingsSection
              title='Team'
              additionalHeaderChild={
                shouldShowInviteButton && (
                  <LiliLinkButton
                    title={t('ACCOUNT_SETTINGS.TEAM.INVITE_BUTTON')}
                    id='invite-button'
                    testId='invite-button'
                    leftIcon={<PlusIcon className='text-action03 w-[16px] h-[16px]' />}
                    type={LiliLinkType.SMART}
                    onClick={() => onInviteClick()}
                  />
                )
              }
            >
              <AccountSettingsRolesAndPermissionsItem onRemove={onRemoveMemberClick} team={teamMembers} />
            </AccountSettingsSection>
            {planUpgradePermission !== FeatureAccess.NONE && (
              <AccountSettingsPlanItem
                onUpgradeClick={onUpgradePlanClick}
                plan={customerProfile}
                planFee={profileFee}
                isAnnual={annual}
              />
            )}
          </div>
        </div>
        <div className='flex flex-col border-t-[1px] border-t-bw03  items-center w-full bg-bw02 '>
          <div className='flex flex-col w-full  max-w-[1300px] px-[48px] mx-[24px]'>
            <AccountSettingsLegalDocs />
            <AccountSettingsSupport
              onItemClick={onSupportItemClick}
              supportEmail={supportEmail}
              supportPhone={supportPhone}
            />
          </div>
        </div>
      </div>
      <AccountSettingsFlowManager
        flowToShow={currentFlow}
        onCloseFlow={() => setCurrentFlow(AccountSettingsFlow.NONE)}
      />
      {teamMemberToDelete && (
        <RemoveTeamMemberModal
          teamMemberToDelete={teamMemberToDelete}
          isLoading={isRemovingMember}
          onCancel={() => setTeamMemberToDelete(null)}
          onSubmit={onRemoveSubmit}
        />
      )}
      {teamFullModal}
    </Layout>
  );
};
